<template>
  <div v-if="loading" class="flex items-center justify-center h-full">
    <loading-spinner diameter="64px" />
  </div>

  <alert-badge
    v-else-if="error.title"
    isDanger
    :title="error.title"
    :body="error.body"
    @dismissed="() => { error.title = ''; error.body = ''; }"
  />

  <div v-else>
    <div class="flex flex-row">
      <div class="flex flex-col">
        <action-button :title="t('print')" faIcon="print" @click="print" />
      </div>

      <template v-if="$ctx.getReceiptSize() == 'a4' || $ctx.getReceiptSize() == 'a5'">
        <VendorBillReceiptA4
          :vendorBill="vendorBill"
          :transactions="transactions"
        />
      </template>

      <template v-if="$ctx.getReceiptSize() == 'receipt'">
        <VendorBillReceiptSmall
          :vendorBill="vendorBill"
          :transactions="transactions"
        />
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import AlertBadge from "@/components/AlertBadge.vue";
import ActionButton from "@/components/ActionButton.vue";
import VendorBill, { vendorBillI18nMessages } from "@/models/vendor/VendorBill";
import { Transaction } from "@/models/accounting/Transaction";
import VendorBillReceiptA4 from "./VendorBillReceiptA4.vue";
import VendorBillReceiptSmall from "./VendorBillReceiptSmall.vue";

export default defineComponent({
  name: 'VendorBillReceipt',

  components: {
    LoadingSpinner,
    AlertBadge,
    ActionButton,
    VendorBillReceiptA4,
    VendorBillReceiptSmall
  },

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...vendorBillI18nMessages.en,
          print: "Print Receipt"
        },
        ar: {
          ...vendorBillI18nMessages.ar,
          print: "اطبع الفاتورة"
        }
      }
    });

    return { t };
  },

  data() {
    return {
      vendorBill: new VendorBill(),
      transactions: new Array<Transaction>(),
      loading: true,
      error: {
        title: "",
        body: ""
      }
    };
  },

  async mounted() {
    try {
      const id = this.$route.params.id as string;
      const transactionID = this.$route.query.transactionID as string | undefined;

      // Load vendor bill
      const vendorBillResp = await this.$http.get<VendorBill>(`${VendorBill.ENDPOINT}/${id}`);
      this.vendorBill = VendorBill.from(vendorBillResp);

      // Load transactions for this vendor bill
      if (this.vendorBill.transactions && this.vendorBill.transactions.length > 0) {
        let allTransactions = this.vendorBill.transactions.map(t => Transaction.from(t)).filter(t => t.isReceipt);

        // If transactionID is provided, filter to show only that transaction
        if (transactionID) {
          this.transactions = allTransactions.filter(t => t.id === transactionID);
        } else {
          this.transactions = allTransactions;
        }
      }
    } catch (error) {
      this.error.title = error.title || 'Error loading vendor bill';
      this.error.body = error.body || '';
    } finally {
      this.loading = false;
    }
  },

  methods: {
    print() {
      window.print();
    }
  }
});
</script>

<style>
</style>
